/**
 * 批次商品列表管理 Hook
 * 用于获取和管理指定批次下的商品素材列表
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { listMaterialsByBatch } from '@/services/materialService'
import type { Material, MaterialListParams } from '@/types/material'

/**
 * useCrawlBatchGoods Hook 的返回值类型
 */
export interface UseCrawlBatchGoodsResult {
  /** 商品列表 */
  items: Material[]
  /** 加载状态 */
  isLoading: boolean
  /** 错误信息 */
  error: string | null
  /** 总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  pageSize: number
  /** 标题搜索关键词 */
  keywordSearch: string
  /** 设置页码 */
  setPage: (page: number) => void
  /** 设置标题搜索 */
  setKeywordSearch: (keyword: string) => void
  /** 刷新列表 */
  refresh: () => Promise<void>
}

/**
 * 批次商品列表管理 Hook
 * @param batchId 批次 ID
 * @param initialPageSize 初始每页数量，默认 20
 * @returns 商品列表状态和操作方法
 */
export function useCrawlBatchGoods(
  batchId: string,
  initialPageSize = 20
): UseCrawlBatchGoodsResult {
  const [items, setItems] = useState<Material[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [pageSize] = useState<number>(initialPageSize)
  const [keywordSearch, setKeywordSearch] = useState<string>('')
  const previousBatchIdRef = useRef<string>(batchId)

  /**
   * 加载批次商品列表
   */
  const loadGoods = useCallback(async (): Promise<void> => {
    if (batchId.trim() === '') {
      setItems([])
      setTotal(0)
      setError(null)
      setIsLoading(false)
      return
    }

    // 切换批次时先重置到第一页，避免沿用上一个批次的页码
    if (previousBatchIdRef.current !== batchId) {
      previousBatchIdRef.current = batchId
      if (page !== 1) {
        setPage(1)
        return
      }
    }

    setIsLoading(true)
    setError(null)

    try {
      const params: MaterialListParams = {
        current: page,
        pageSize,
      }

      if (keywordSearch.trim() !== '') {
        params.title = keywordSearch.trim()
      }

      const response = await listMaterialsByBatch(batchId, params)

      setItems(response.list)
      setTotal(response.total)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '获取商品列表失败'
      setError(errorMessage)
      setItems([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [batchId, page, pageSize, keywordSearch])

  /**
   * 刷新列表
   */
  const refresh = useCallback(async (): Promise<void> => {
    await loadGoods()
  }, [loadGoods])

  /**
   * 监听依赖变化，自动加载数据
   */
  useEffect((): void => {
    void loadGoods()
  }, [loadGoods])

  return {
    items,
    isLoading,
    error,
    total,
    page,
    pageSize,
    keywordSearch,
    setPage,
    setKeywordSearch,
    refresh,
  }
}
